import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { fetchQuote } from '../Api/QuoteApi';
import { getPromo, applyPromo, promoDiscount } from '../_lib/promos';
import { LEGACY_PRE_FEE_SERVICE_AGREEMENT } from '../_lib/agreement-clauses-legacy';
import { normalizeVisitsPerWeek } from '../_lib/agreement-clauses';
import { seasonalSummaryLines } from '../_lib/seasonal-pricing';
import '../styles/quote-page.css';
import '../styles/agreement-view.css';

const formatPacific = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
};

const AgreementView = ({ config }) => {
  const { dealId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printFallback, setPrintFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPrintFallback(false);
    fetchQuote(dealId, { context: 'agreement' })
      .then((data) => {
        if (cancelled) return;
        setQuote(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.status === 404 ? 'Agreement not found.' : 'Unable to load agreement.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dealId]);

  if (loading) {
    return (
      <div className="qp-loading">
        <img src={config.logo} alt={config.name} className="qp-loading-logo" />
        <div className="qp-loading-spinner" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="qp-error-page">
        <img src={config.logo} alt={config.name} className="qp-loading-logo" />
        <h2>{error || 'Agreement not found.'}</h2>
      </div>
    );
  }

  // Accept both terminal 'signed' and intermediate 'signing'. The
  // server only writes 'signing' after the snapshot has been persisted
  // and a signature timestamp recorded, so the customer sees their
  // signed copy immediately even if Sell stage update or one of the
  // other downstream side effects is still completing.
  const isSignedState = (quote.status === 'signed' || quote.status === 'signing');
  if (!isSignedState || !quote.signedAt) {
    return (
      <div className="qp-error-page">
        <img src={config.logo} alt={config.name} className="qp-loading-logo" />
        <h2>This agreement has not been signed yet.</h2>
        <p>If you intended to view your signed copy, please sign the agreement at <a href={`/q/${dealId}`}>your quote page</a> first.</p>
      </div>
    );
  }

  // Prefer the durable snapshot stored at sign time. Records without a
  // snapshot are legacy signings from before 2026-05-03 (when the
  // snapshot shipped); for those, fall back to the FROZEN pre-fee
  // clause set, never the live brand config. The live copy changes
  // over time (e.g. the 2026-08-17 card-fee disclosure) and rendering
  // it here would retroactively rewrite the customer's permanent
  // signed record with terms they never agreed to. Every no-snapshot
  // record predates the fee disclosure, so the frozen set is correct
  // for all of them. Net-30 shipped 2026-05-27, after snapshots, so a
  // no-snapshot net-30 record should not exist; branch for it anyway
  // so the fallback can never show card-charge terms on one.
  const snap = quote.signedAgreementSnapshot || null;
  const isMonthly = !!quote.hasMonthlyService;
  const legacySa = LEGACY_PRE_FEE_SERVICE_AGREEMENT;
  const legacyIsNet30 = !isMonthly && quote.paymentTerms === 'net_30';
  // Pay-by-check is monthly-only and shipped after snapshots, so a
  // no-snapshot check record cannot exist. If one somehow did, the frozen
  // legacy set would show auto-pay charge terms on an account that has no
  // payment method, so fall back to the live check clauses instead: they are
  // the only version of that agreement that has ever been signable, which
  // makes them a faithful record rather than a retroactive rewrite.
  const legacyIsCheck = isMonthly && quote.paymentTerms === 'check';
  const checkSa = config.serviceAgreement || {};
  const liveCards = legacyIsCheck
    ? (checkSa.monthlyCheckCards || legacySa.cards)
    : isMonthly
    ? legacySa.cards
    : legacyIsNet30
      ? (legacySa.oneTimeNet30Cards || legacySa.oneTimeCards || legacySa.cards)
      : (legacySa.oneTimeCards || legacySa.cards);
  const liveConsentText = legacyIsCheck
    ? (checkSa.monthlyCheckConsentText || legacySa.consentText)
    : isMonthly
    ? legacySa.consentText
    : legacyIsNet30
      ? (legacySa.oneTimeNet30ConsentText || legacySa.oneTimeConsentText || legacySa.consentText)
      : (legacySa.oneTimeConsentText || legacySa.consentText);

  // Snapshot wins (immutable signed record); live cards are filtered
  // by tier so chem-only / chem-and-brush quotes opt out of clauses
  // marked excludeForTiers (e.g. weather-conditions, which is full-
  // service scope). The snapshot is itself already filtered at sign
  // time so no second filter pass is needed there.
  const tierForFilter = quote.serviceTier || 'full';
  const filteredLiveCards = liveCards.filter(
    (c) => !(Array.isArray(c.excludeForTiers) && c.excludeForTiers.includes(tierForFilter))
  );
  const cards = (snap && Array.isArray(snap.cards) && snap.cards.length > 0) ? snap.cards : filteredLiveCards;
  const consentText = (snap && snap.consentText) ? snap.consentText : liveConsentText;
  const intro = (snap && snap.intro) ? snap.intro : legacySa.intro;
  const esignDisclosure = (snap && snap.esignDisclosure)
    ? snap.esignDisclosure
    : (isMonthly
      ? legacySa.esignDisclosure
      : legacyIsNet30
        ? (legacySa.oneTimeNet30EsignDisclosure || legacySa.oneTimeEsignDisclosure || legacySa.esignDisclosure)
        : (legacySa.oneTimeEsignDisclosure || legacySa.esignDisclosure));
  const agreementVersion = (snap && snap.agreementVersion) || quote.agreementVersion || '';

  // Prefer snapshot pricing fields too. The snapshot persists what the
  // customer actually saw on the page; the top-level quote fields are
  // mutable (publisher CF could overwrite them) and live data should
  // never retroactively rewrite a signed record.
  const snapPricing = snap?.pricing || null;
  const oneTimeServices = (snapPricing && Array.isArray(snapPricing.oneTimeServices))
    ? snapPricing.oneTimeServices
    : (Array.isArray(quote.oneTimeServices) ? quote.oneTimeServices : []);
  const oneTimeTotal = oneTimeServices.reduce((s, x) => s + Number(x.price || 0), 0);
  const weeklyPrice = snapPricing?.weeklyPrice ?? quote.weeklyPrice ?? 0;
  const monthlyPrice = snapPricing?.monthlyPrice ?? quote.monthlyPrice ?? 0;
  const serviceTier = snapPricing?.tier ?? quote.serviceTier ?? '';
  // Cadence was weekly before snapshots captured it. Never let a later
  // edit to the quote rewrite the frequency of an existing signed record.
  const visitsPerWeek = normalizeVisitsPerWeek(snapPricing?.visitsPerWeek);
  const tierLabelBase = config.serviceTierLabels?.[serviceTier] || 'Weekly Service';
  const tierLabel = visitsPerWeek === 0.5
    ? `${tierLabelBase.replace(/^Weekly\s+/i, '')}, every other week`
    : visitsPerWeek === 2
    ? `${tierLabelBase.replace(/^Weekly\s+/i, '')}, twice weekly`
    : tierLabelBase;
  const serviceAddress = snap?.serviceAddress ?? quote.address ?? '';
  // Seasonal terms come only from the signed snapshot, like cadence above.
  const seasonalLines = snapPricing?.seasonalPricing ? seasonalSummaryLines(snapPricing.seasonalPricing) : [];

  // Promo: read from the immutable snapshot first (what the customer
  // actually signed), fall back to the live quote field. Discount lines
  // up against the relevant invoice based on the promo's appliesTo:
  //   - monthly_first_month: deducts from the first monthly invoice only
  //   - invoice_total: deducts from one-time + first monthly together
  // Always floored at $0 by promoDiscount(); the unused remainder (if
  // the credit exceeds the invoice) is tracked out-of-band by sales.
  const promoCode = snap?.promo ?? quote.promo ?? null;
  const promoCfg = getPromo(promoCode);
  const promoInvoiceBase = promoCfg?.appliesTo === 'invoice_total'
    ? oneTimeTotal + Number(monthlyPrice || 0)
    : Number(monthlyPrice || 0);
  const promoApplied = promoCfg ? promoDiscount(promoInvoiceBase, promoCode) : 0;
  const dueToday = promoCfg
    ? applyPromo(promoInvoiceBase, promoCode)
    : promoInvoiceBase;

  const renderedExtras = (snap && Array.isArray(snap.renderedExtras))
    ? snap.renderedExtras
    : (Array.isArray(quote.renderedExtras) ? quote.renderedExtras : []);
  const payment = (snap && snap.paymentSummary) || quote.paymentSummary || null;
  // Net 30 deals don't capture a payment method; render the terms
  // instead so the customer's signed copy reflects what they actually
  // agreed to. Snapshot is authoritative; fall back to the live quote
  // flag for legacy entries signed before the snapshot started capturing
  // paymentTerms.
  const isNet30 = (snap?.paymentTerms || quote.paymentTerms) === 'net_30';
  // Pay-by-check monthly deals capture no payment method either. The check
  // variant shipped after signed snapshots did, so a check record always has
  // a snapshot and the legacy fallback above is unreachable for one; the
  // branch below exists so the label can never claim a card is on file.
  const isCheck = (snap?.paymentTerms || quote.paymentTerms) === 'check';

  // The card-fee note is gated on the agreement version: only signings
  // whose agreed terms included the 2026-08-17 fee disclosure get the
  // label. Earlier card signings do pay a processing fee via the
  // billing templates (mostly 3.5%, a few grandfathered at 3%), but
  // this page is the record of what was signed, so it must not assert
  // terms the record does not contain. ISO date strings compare
  // correctly as strings.
  const signedWithFeeDisclosure = String(agreementVersion || '') >= '2026-08-17';
  const paymentLabel = isCheck
    ? 'Invoiced monthly, paid by check (no card or bank account on file)'
    : isNet30
    ? 'Net 30, invoiced at completion'
    : payment
      ? payment.method === 'cc'
        ? `${payment.brand || 'Card'} ending in ${payment.last4 || '****'}${signedWithFeeDisclosure ? ' (3.5% card processing fee applies)' : ''}`
        : `Bank account on file (ACH)${payment.last4 ? ` ...${payment.last4}` : ''}`
      : 'No payment method on file';

  // In-app browsers (Google app, Instagram, some mail clients) replace
  // window.print with a shim that calls an unregistered
  // window.webkit.messageHandlers.print handler and throws, leaving the
  // button a silent no-op (Sentry CABANA-QUOTE-FORM-8). The fallback
  // renders in-page rather than via alert() because the same webviews
  // may not implement the JS dialog delegate either, which would make
  // an alert a second silent no-op. The handled Sentry event keeps the
  // degraded path observable without re-firing the unhandled error.
  const handlePrint = () => {
    try {
      window.print();
      setPrintFallback(false);
    } catch (err) {
      setPrintFallback(true);
      Sentry.captureMessage('agreement_print_fallback', {
        level: 'warning',
        tags: { stage: 'agreement_print' },
        extra: { error: String(err) },
      });
    }
  };

  return (
    <div className="qp-page qp-agreement-view-only">
      <header className="av-header">
        <img src={config.logo} alt={config.name} className="av-logo" />
        <div className="av-header-text">
          <div className="av-eyebrow">Signed Service Agreement</div>
          <h1 className="av-title">{quote.contactName || ''}</h1>
          <div className="av-meta">Deal {quote.dealId || dealId}</div>
        </div>
        <button
          type="button"
          className="av-print-btn no-print"
          onClick={handlePrint}
        >
          Print / Save PDF
        </button>
      </header>

      {printFallback && (
        <div className="av-print-fallback no-print" role="alert">
          Printing isn't supported in this in-app browser. Tap the Share
          button or the three-dot menu and choose "Open in browser" or
          "Open in Safari", then print or save from there.
        </div>
      )}

      <section className="av-section">
        <h2 className="qp-section-title">Pricing summary</h2>
        <div className="av-pricing">
          {isMonthly && (
            <div className="av-pricing-row">
              <span className="av-pricing-label">
                {tierLabel}
                {(() => {
                  if (serviceTier === 'spa_only') return ' (Spa only)';
                  const hasPool = isMonthly || oneTimeServices.length > 0;
                  const scope = quote.hasSpa && hasPool ? 'Pool & Spa'
                    : quote.hasSpa ? 'Spa only'
                    : 'Pool only';
                  return ` (${scope})`;
                })()}
              </span>
              <span className="av-pricing-value">{snapPricing?.seasonalPricing && [snapPricing.seasonalPricing.summer, snapPricing.seasonalPricing.winter].some(s => s.visitsPerWeek === 0.5) ? `$${monthlyPrice / (4 * visitsPerWeek)}/visit` : `$${weeklyPrice}/week`}, ${monthlyPrice}/month</span>
            </div>
          )}
          {isMonthly && seasonalLines.length > 0 && (
            <ul className="av-onetime-list">
              {seasonalLines.map((line) => <li key={line}>{line}</li>)}
            </ul>
          )}
          {oneTimeTotal > 0 && (
            <div className="av-pricing-row">
              <span className="av-pricing-label">One-time services</span>
              <span className="av-pricing-value">${oneTimeTotal}</span>
            </div>
          )}
          {oneTimeServices.length > 0 && (
            <ul className="av-onetime-list">
              {oneTimeServices.map((s, i) => (
                <li key={i}>{s.name}{s.price ? ` ($${s.price})` : ''}</li>
              ))}
            </ul>
          )}
          {promoCfg && promoApplied > 0 && (
            <>
              <div className="av-pricing-row av-pricing-promo">
                <span className="av-pricing-label">{promoCfg.agreementLabel}</span>
                <span className="av-pricing-value">-${promoApplied}</span>
              </div>
              <div className="av-pricing-row av-pricing-total">
                <span className="av-pricing-label">
                  <strong>{promoCfg.appliesTo === 'monthly_first_month' ? 'First month due' : 'Amount due today'}</strong>
                </span>
                <span className="av-pricing-value"><strong>${dueToday}</strong></span>
              </div>
            </>
          )}
          <div className="av-pricing-row">
            <span className="av-pricing-label">Service address</span>
            <span className="av-pricing-value">{serviceAddress}</span>
          </div>
          <div className="av-pricing-row">
            <span className="av-pricing-label">{(isNet30 || isCheck) ? 'Payment terms' : 'Payment on file'}</span>
            <span className="av-pricing-value">{paymentLabel}</span>
          </div>
        </div>
      </section>

      <section className="av-section">
        <h2 className="qp-section-title">Agreement clauses</h2>
        <p className="qp-agreement-intro">{intro}</p>
        <div className="av-clauses">
          {cards.map((card, i) => (
            <div key={i} className="qp-agreement-card av-clause">
              <div className="qp-agreement-card-title">{card.title}</div>
              <div className="qp-agreement-card-body">{card.body}</div>
              {card.details && (
                <div className="qp-agreement-card-details av-clause-details">{card.details}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {renderedExtras.length > 0 && (
        <section className="av-section">
          <h2 className="qp-section-title">Specialty treatments billed separately</h2>
          <table className="av-extras-table">
            <thead>
              <tr>
                <th>Treatment</th>
                <th>Frequency</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              {renderedExtras.map((e, i) => (
                <tr key={i}>
                  <td>{e.name}</td>
                  <td className="av-extras-freq">{e.frequency}</td>
                  <td className="av-extras-price">{e.priceLabel || (e.price ? `$${e.price}` : '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="av-section av-consent-box">
        <h2 className="qp-section-title">{isCheck
          ? 'Payment terms'
          : isMonthly
            ? 'Auto-pay authorization'
            : isNet30
              ? 'Net 30 payment terms'
              : 'Charge authorization'}</h2>
        <p>{consentText}</p>
      </section>

      <section className="av-section av-esign-record">
        <h2 className="qp-section-title">ESIGN signature record</h2>
        <dl className="av-esign-grid">
          <dt>Signed by</dt>
          <dd>{quote.contactName || ''} &lt;{quote.contactEmail || ''}&gt;</dd>
          <dt>Signed at</dt>
          <dd>{formatPacific(quote.signedAt)} ({quote.signedAt})</dd>
          <dt>Deal ID</dt>
          <dd>{quote.dealId || dealId}</dd>
          <dt>Agreement version</dt>
          <dd>{agreementVersion || '(unspecified)'}</dd>
          <dt>Consent text</dt>
          <dd className="av-consent-text">{consentText}</dd>
        </dl>
        <p className="av-esign-disclosure">{esignDisclosure}</p>
      </section>

      <footer className="av-footer">
        <div>{config.name} • {config.website} • {config.phone}</div>
      </footer>
    </div>
  );
};

export default AgreementView;
